import { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

function digitsOnly(s = "") {
  return String(s).replace(/\D/g, "");
}
function waLink(
  anyPhone = "",
  defaultText = "Hi, I’m interested in this card."
) {
  const digits = digitsOnly(anyPhone);
  if (!digits) return null;
  const text = encodeURIComponent(defaultText);
  return `https://wa.me/${digits}?text=${text}`;
}
function Avatar({ src, fallback }) {
  return (
    <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-700 flex items-center justify-center">
      {src ? (
        <img alt="owner" src={src} className="w-full h-full object-cover" />
      ) : (
        <span className="text-slate-200 text-xs font-semibold">{fallback}</span>
      )}
    </div>
  );
}

/**
 * Props:
 * - cardId?: string
 * - card?: { agentUid?, agentName?, agentPhotoURL?, agentWhatsApp? }
 *
 * It will read agents/{agentUid} if needed and prefers values on the card.
 */
export default function AgentOwnerBadge({ cardId, card: cardProp }) {
  const [card, setCard] = useState(cardProp || null);
  const [agent, setAgent] = useState(null);

  // load card if only id provided
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!cardId || cardProp) return;
      const s = await getDoc(doc(db, "cards", cardId));
      if (!ignore && s.exists()) setCard({ id: s.id, ...s.data() });
    })();
    return () => {
      ignore = true;
    };
  }, [cardId, cardProp]);

  // load agent if needed
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!card?.agentUid) return;
      const s = await getDoc(doc(db, "agents", card.agentUid));
      if (!ignore && s.exists()) setAgent({ id: s.id, ...s.data() });
    })();
    return () => {
      ignore = true;
    };
  }, [card?.agentUid]);

  if (!card) return null;

  const displayName =
    card.agentName ||
    agent?.fullName ||
    agent?.username ||
    agent?.name ||
    agent?.email ||
    (card.agentUid ? card.agentUid.slice(0, 6) : "Owner");

  const photo =
    card.agentPhotoURL ||
    agent?.photoURL ||
    agent?.avatarUrl ||
    agent?.imageUrl ||
    "";

  const wa =
    card.agentWhatsApp ||
    agent?.whatsapp ||
    agent?.phoneE164 ||
    agent?.phone ||
    agent?.mobile ||
    "";

  const href = waLink(wa, "Hi! I’m viewing your lottery card.");
  const initials = String(displayName || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-800 ring-1 ring-slate-700">
      <Avatar src={photo} fallback={initials} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate">
          {displayName}
        </div>
        {wa ? (
          <div className="text-xs text-slate-300 truncate">
            WhatsApp: <span className="font-mono">{wa}</span>
          </div>
        ) : (
          <div className="text-xs text-slate-400">WhatsApp not set</div>
        )}
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 rounded-xl bg-lime-400 text-black text-sm font-semibold"
        >
          WhatsApp
        </a>
      ) : null}
    </div>
  );
}
